import type { SearchArtistItemDto } from '@customTypes/types.ts';
import { useIntersection } from '@mantine/hooks';
import { keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { useSearchArtists } from '@/features/search/api';
import { useIsLoading } from '@/shared/hooks/useIsLoading';

export const useClubSearchPage = () => {
  const [originalArtistsList, setOriginalArtistsList] = useState<
    SearchArtistItemDto[]
  >([]);

  const search = useSearch({ from: '/clubOwner/$id/_club/search/' });
  const navigate = useNavigate({ from: '/clubOwner/$id/search' });
  // const navigate = useNavigate({ from: '/clubOwner/$id/_club/search' });

  const citizenships = useMemo(
    () => search.citizenships?.split(',') ?? [],
    [search.citizenships]
  );

  const setCitizenships = (newValues: string[]) => {
    navigate({
      search: {
        citizenships: newValues.length > 0 ? newValues.join(',') : undefined
      }
    });
  };

  const { ref: elementRef, entry } = useIntersection();
  const countryParams = useMemo(
    () => (citizenships?.length > 1 ? citizenships.join(',') : citizenships[0]),
    [citizenships]
  );

  const artists = useSearchArtists(
    {
      citizenships: countryParams
    },
    {
      options: {
        placeholderData: keepPreviousData
      }
    }
  );

  // Fetch next page when intersecting
  useEffect(() => {
    if (entry?.isIntersecting) {
      artists.fetchNextPage();
    }
  }, [entry?.isIntersecting]);

  // Flatten loaded pages
  const posts = useMemo(
    () => artists.data?.pages.flatMap((page) => page.data.items) || [],
    [artists.data?.pages]
  );

  // Update originalArtistsList only if no filter is applied
  useEffect(() => {
    if (citizenships.length === 0 && posts.length > 0) {
      setOriginalArtistsList((prev) => [...prev, ...posts]);
    }
  }, [citizenships, posts]);

  const isLoading = useIsLoading([artists]);

  return {
    state: {
      isLoading,
      citizenships,
      setCitizenships,
      artistsList: posts,
      originalArtistsList,
      elementRef
    },
    functions: {
      navigate
    }
  };
};
